import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api, UnauthorizedError } from "@/api/client";
import type { SharedConversation } from "@/api/types";
import { Loader2, AlertTriangle, Lock } from "lucide-react";

type ErrKind = null | "login" | "gone" | "notfound";

export default function SharedView() {
  const { token = "" } = useParams();
  const [data, setData] = useState<SharedConversation | null>(null);
  const [err, setErr] = useState<ErrKind>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setErr(null);
    api
      .shared(token)
      .then(setData)
      .catch((e: unknown) => {
        if (e instanceof UnauthorizedError) setErr("login");
        else if (e instanceof Error && /만료/.test(e.message)) setErr("gone");
        else setErr("notfound");
      })
      .finally(() => setLoading(false));
  }, [token]);

  const loginUrl = api.loginUrl(window.location.pathname);

  return (
    <div className="flex h-full flex-col">
      <header className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-background/90 px-4 py-3 backdrop-blur">
        <img src="/icon-512.png" alt="ArkiveAI" className="h-6 w-6 rounded-md" />
        <span className="font-bold text-[#0E1E2D]">ArkiveAI</span>
        <span className="text-sm text-muted-foreground">· 공유된 대화 (읽기 전용)</span>
      </header>

      <main className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> 불러오는 중…
          </div>
        ) : err === "login" ? (
          <div className="mx-auto flex h-full max-w-lg flex-col items-center justify-center gap-3 px-6 text-center text-muted-foreground">
            <Lock className="h-8 w-8" />
            <p>사내 로그인이 필요한 공유입니다.</p>
            <a
              href={loginUrl}
              className="rounded-lg bg-[#0E1E2D] hover:bg-[#1C3954] px-4 py-2 text-sm font-medium text-white"
            >
              사내 계정으로 로그인
            </a>
          </div>
        ) : err === "gone" ? (
          <div className="mx-auto flex h-full max-w-lg flex-col items-center justify-center gap-3 px-6 text-center text-muted-foreground">
            <AlertTriangle className="h-8 w-8" />
            <p>공유 기간이 만료되었습니다.</p>
          </div>
        ) : err || !data ? (
          <div className="mx-auto flex h-full max-w-lg flex-col items-center justify-center gap-3 px-6 text-center text-muted-foreground">
            <AlertTriangle className="h-8 w-8" />
            <p>공유 대화를 찾을 수 없거나 공유가 해제되었습니다.</p>
          </div>
        ) : (
          <div className="mx-auto w-full max-w-3xl px-4 py-4">
            <h1 className="mb-1 text-lg font-bold">{data.title}</h1>
            <p className="mb-4 text-xs text-muted-foreground">
              공유일 {data.created_at?.slice(0, 16).replace("T", " ")}
            </p>
            <div className="flex flex-col gap-4">
              {data.turns.map((t, i) =>
                t.role === "user" ? (
                  <div key={i} className="flex justify-end">
                    <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-[#0E1E2D] px-3.5 py-2 text-sm text-white">
                      {t.text}
                    </div>
                  </div>
                ) : (
                  <div key={i} className="flex justify-start">
                    <div className="md max-w-[92%] rounded-2xl rounded-bl-sm border border-border bg-card px-3.5 py-2.5 text-sm text-card-foreground">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{t.text}</ReactMarkdown>
                    </div>
                  </div>
                ),
              )}
            </div>
            <div className="mt-8 border-t border-border pt-4 text-center text-xs text-muted-foreground">
              ArkiveAI 지식 Q&amp;A · 이 페이지는 공유된 대화의 사본입니다.
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
